import { Network } from '@capacitor/network'

// Network status interface
export interface NetworkStatus {
  connected: boolean
  connectionType: string
  hasInternetAccess: boolean
}

// Event listeners type
type NetworkChangeListener = (status: NetworkStatus) => void

/**
 * Service for monitoring network connectivity
 * Provides real-time network status and change notifications
 */
export class NetworkMonitor {
  private listeners: Set<NetworkChangeListener> = new Set()
  private currentStatus: NetworkStatus | null = null
  private isInitialized = false

  /**
   * Initialize the network monitor
   * Sets up network change listeners and gets initial status
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return

    try {
      // Get initial network status
      const status = await Network.getStatus()
      this.currentStatus = {
        connected: status.connected,
        connectionType: status.connectionType,
        hasInternetAccess: status.connected, // Assume internet access if connected
      }

      // Listen for network changes
      Network.addListener('networkStatusChange', (status) => {
        const newStatus: NetworkStatus = {
          connected: status.connected,
          connectionType: status.connectionType,
          hasInternetAccess: status.connected,
        }

        const wasOnline = this.currentStatus?.connected || false
        const isNowOnline = newStatus.connected

        this.currentStatus = newStatus

        // Log network changes
        if (wasOnline !== isNowOnline) {
          if (isNowOnline) {
            console.log('🌐 NetworkMonitor: Back online', newStatus)
          } else {
            console.log('📴 NetworkMonitor: Gone offline', newStatus)
          }
        }

        // Notify all listeners
        this.listeners.forEach((listener) => {
          try {
            listener(newStatus)
          } catch (error) {
            console.error('❌ NetworkMonitor: Error in listener callback', error)
          }
        })
      })

      this.isInitialized = true
      console.log('✅ NetworkMonitor: Initialized', this.currentStatus)
    } catch (error) {
      console.error('❌ NetworkMonitor: Failed to initialize', error)
      // Assume online if we can't detect network status
      this.currentStatus = {
        connected: true,
        connectionType: 'unknown',
        hasInternetAccess: true,
      }
    }
  }

  /**
   * Get current network status
   * @returns Current network status or null if not initialized
   */
  getCurrentStatus(): NetworkStatus | null {
    return this.currentStatus
  }

  /**
   * Check if currently online
   * @returns true if connected to network
   */
  isOnline(): boolean {
    // If not initialized yet, assume online to allow attempt
    if (!this.isInitialized || !this.currentStatus) {
      console.warn('⚠️ NetworkMonitor: Status requested before initialization - assuming online')
      return true
    }
    return this.currentStatus.connected
  }

  /**
   * Check if has internet access
   * @returns true if has internet access
   */
  hasInternetAccess(): boolean {
    return this.currentStatus?.hasInternetAccess || false
  }

  /**
   * Add a listener for network status changes
   * @param listener - Function to call when network status changes
   * @returns Function to remove the listener
   */
  addListener(listener: NetworkChangeListener): () => void {
    this.listeners.add(listener)

    // Call listener immediately with current status if available
    if (this.currentStatus) {
      try {
        listener(this.currentStatus)
      } catch (error) {
        console.error('❌ NetworkMonitor: Error in initial listener call', error)
      }
    }

    // Return cleanup function
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): void {
    this.listeners.clear()
  }

  /**
   * Manually refresh network status
   * Useful for checking connectivity before important operations
   */
  async refresh(): Promise<NetworkStatus> {
    try {
      const status = await Network.getStatus()
      this.currentStatus = {
        connected: status.connected,
        connectionType: status.connectionType,
        hasInternetAccess: status.connected,
      }
      return this.currentStatus
    } catch (error) {
      console.error('❌ NetworkMonitor: Failed to refresh status', error)
      return (
        this.currentStatus || {
          connected: true,
          connectionType: 'unknown',
          hasInternetAccess: true,
        }
      )
    }
  }
}

// Export singleton instance
export const networkMonitor = new NetworkMonitor()
